<template>
  <div
    class="text-base text-gray-500 min-h-screen flex flex-col bg-appBg"
    :class="{ 'md:h-screen md:overflow-hidden': usesSableViewportShell, isolate: isSableAuthPage || isSableEasyCheckout }"
    :dir="$store.state.currentLocale?.direction"
  >
    <a href="#main-content" class="skip-link">Skip to main content</a>
    <config-error v-if="$store.getters.configError"></config-error>
    <config-loading v-if="!$store.getters.config && !$store.getters.configError"></config-loading>

    <div v-if="$store.getters.config && !$store.getters.configError" data-cy="header-container" :class="{ contents: isSableProfile }">
      <header-entry :hide-header="!showHeader"></header-entry>
    </div>
    <main
      class="w-full flex-1 mx-auto"
      :class="{
        'px-4 lg:px-5': !isSableTheme,
        'px-4 lg:px-2': isSableTheme && !isSableJoinFlow && !isSableAuthPage,
        'flex flex-col': isSableAuthPage || isSableEasyCheckout,
        'md:flex md:min-h-0 md:flex-col md:overflow-y-auto sable-rail-scroll': usesSableViewportShell,
        'max-w-7xl': !isSableJoinFlow && !isSableAuthPage,
        '2xl:max-w-[90rem]': isSableTheme && !isSableJoinFlow && !isSableAuthPage,
      }"
      id="main-content"
      data-cy="main-content"
      role="main"
      tabindex="-1"
    >
      <router-view v-if="$store.getters.config && !$store.getters.configError" />
    </main>
    <div
      id="bottom-nav"
      :class="{
        'sticky bottom-0 z-50': $store.state.config && !$store.getters.configError && $store.state.config.joinFlow.enableBannerSummary,
      }"
    ></div>
    <div class="" data-cy="the-footer" id="app-footer" v-if="showFooter">
      <the-footer id="theFooter" v-if="$store.getters.config && !$store.getters.configError"></the-footer>
    </div>
  </div>
</template>

<script lang="ts" src="./App.ts" />

<style lang="less">
@tailwind components;
@layer components {
  .h1 {
    @apply text-center font-bold text-4xl;
  }

  /* Common focus style for primary and opposite colored elements */
  .focus-primary {
    @apply focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-500;
  }

  .focus-opposite {
    @apply focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-opposite-500;
  }

  .focus-gray {
    @apply focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-700;
  }

  .focus-ring-primary {
    @apply rounded-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary-500;
  }

  .focus-ring-opposite {
    @apply rounded-full focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-opposite-500;
  }
  /* Button for positive actions; Confirm, Yes, Select, Login, Book etc. */
  .btn-positive {
    @apply bg-primary-500 hover:bg-primary-600 text-base text-opposite-500 text-center px-4 py-2 rounded cursor-pointer focus-primary;
    &[disabled] {
      @apply bg-gray-200 text-gray-700 cursor-default hover:bg-gray-200 focus-visible:ring-0;
    }
  }

  /* Button for negative actions; Cancel, No, Abort etc. */
  .btn-negative {
    @apply bg-gray-500 hover:bg-gray-600 text-base text-white text-center px-4 py-2 rounded cursor-pointer focus-gray;
    &[disabled] {
      @apply bg-gray-200 text-gray-700 cursor-default hover:bg-gray-200 focus-visible:ring-0;
    }
  }

  .btn-primary-outline {
    @apply bg-transparent hover:bg-primary-600 border-2 border-primary-500 text-base text-primary-500 hover:text-white text-center px-4 py-2 rounded cursor-pointer focus-primary;
    &[disabled] {
      @apply bg-gray-200 text-gray-700 border-gray-200 cursor-default hover:bg-gray-200 focus-visible:ring-0;
    }
  }

  .btn-ghost {
    @apply text-gray-400 hover:text-gray-600 cursor-pointer focus-primary;
    &[disabled] {
      @apply text-gray-200 cursor-default hover:text-gray-200 focus-visible:ring-0;
    }
  }

  .btn-xl {
    @apply px-8 py-4 text-xl;
  }
  .btn-sm {
    @apply px-2 py-1 text-base;
  }

  .input {
    @apply appearance-none border rounded w-full e('py-1.5') px-3 text-gray-700 leading-tight focus-primary;
  }

  /* Visually hidden skip link that appears on focus */
  .skip-link {
    @apply absolute top-auto w-px h-px overflow-hidden;
    left: -9999px;
  }
  .skip-link:focus {
    @apply w-auto h-auto bg-opposite-500 text-primary-700 px-4 py-2 rounded shadow outline-none ring-2 ring-offset-2 ring-primary-500;
    left: 1rem;
    top: 1rem;
    z-index: 1000;
  }

  select {
    @apply pr-10 bg-no-repeat appearance-none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='2' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
    background-position: right 0.75rem center;
    background-size: 1rem 1rem;
  }

  [dir='rtl'] select {
    background-position: left 0.75rem center;
  }

  // global style for input type date with rtl lang
  [dir='rtl'] input[type='date'].join-input-field {
    direction: rtl;
    text-align: right;
  }
  input[type='date']:disabled::-webkit-inner-spin-button,
  input[type='date']:disabled::-webkit-calendar-picker-indicator {
    display: none;
    -webkit-appearance: none;
  }

  .destructive-icon {
    position: relative;
  }

  .destructive-icon::before {
    content: '';
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);

    width: 1rem;
    height: 1rem;
    border: 2px solid currentColor;
    border-radius: 50%;
  }

  .destructive-icon::after {
    content: '';
    position: absolute;
    left: 1rem;
    top: 50%;
    width: 1rem;
    height: 1rem;
    transform: translateY(-50%);

    background: linear-gradient(45deg, transparent 40%, currentColor 40%, currentColor 60%, transparent 60%),
      linear-gradient(-45deg, transparent 40%, currentColor 40%, currentColor 60%, transparent 60%);

    background-size: 45% 45%;
    background-position: center;
    background-repeat: no-repeat;
  }
  // show no scrollbar for the scrollable elements
  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
}
</style>
